import jsPDF from 'jspdf';
import { applyPlugin } from 'jspdf-autotable';
import { formatDate, amountInWords, sv, getItems, getBuyerDetailsText, LW, M, FONT } from './helpers';

applyPlugin(jsPDF);

// ═══════════════════════════════════════════════════════════════════════════════
// PROFORMA INVOICE PDF
// Layout follows the legacy do_proforma.php document:
//   Exporter/Consignor | Proforma Invoice No & Dt + Exporter Reference
//   Buyer/Consignee    | If buyer other than consignee
//   Country/Mode/From/To | Terms of delivery + Payment terms
//   Items (Marks & Nos [left blank], Description, H.S. Code, Qty, Rate, Amount)
//   Amount in words + Total | Advising Bank + Terms | Declarations + Signature
// ═══════════════════════════════════════════════════════════════════════════════

export function generateProformaInvoicePDF(document, company, authority, returnBlob = false) {
  const doc = new jsPDF('p', 'mm', 'a4');
  const pw = doc.internal.pageSize.getWidth();
  const ph = doc.internal.pageSize.getHeight();
  const cw = pw - M * 2; // content width

  doc.setLineWidth(LW);
  doc.setDrawColor(0);

  let y = M;

  // ── Title (outside border) ─────────────────────────────────────────────
  doc.setFontSize(14);
  doc.setFont(FONT, 'bold');
  doc.text('P R O F O R M A   I N V O I C E', pw / 2, y + 5, { align: 'center' });
  y += 10;

  const borderStartY = y;

  // ── Row 1: Exporter/Consignor | Proforma Invoice No & Dt | Exporter Ref ──
  const col1W = cw * 0.45;
  const col2W = cw * 0.35;
  const col3W = cw * 0.20;

  const exporterName = sv(document.exporter_name, sv(company.exporter_name));
  const exporterAddress = sv(document.exporter_address, sv(company.exporter_address));
  const exporterAddrText = exporterAddress +
    (company.email ? ` E MAIL:${sv(company.email)}` : '') +
    (company.gstin ? `, GSTIN: ${sv(company.gstin)}` : '');
  doc.setFontSize(8);
  doc.setFont(FONT, 'bold');
  const exporterNameLines = doc.splitTextToSize(exporterName, col1W - 4);
  doc.setFont(FONT, 'normal');
  const exporterAddrLines = doc.splitTextToSize(exporterAddrText, col1W - 4);

  const row1H = Math.max((exporterNameLines.length + exporterAddrLines.length) * 3.5 + 4, 28);

  // Exporter/Consignor cell
  doc.rect(M, y, col1W, row1H);
  doc.setFontSize(8);
  doc.setFont(FONT, 'bold');
  doc.text('Exporter/ Consignor :', M + 2, y + 4);
  doc.text(exporterNameLines, M + 2, y + 8);
  doc.setFont(FONT, 'normal');
  doc.text(exporterAddrLines, M + 2, y + 8 + exporterNameLines.length * 3.5);

  // Proforma Invoice No cell
  doc.rect(M + col1W, y, col2W, row1H);
  doc.setFont(FONT, 'bold');
  doc.text('Proforma Invoice No. & Dt. :', M + col1W + 2, y + 4);
  doc.setFont(FONT, 'normal');
  doc.text(`${sv(document.invoice_no)} DT. ${formatDate(document.document_date)}`, M + col1W + 2, y + 8);

  // Exporter Reference cell
  doc.rect(M + col1W + col2W, y, col3W, row1H);
  doc.setFont(FONT, 'bold');
  doc.text('Exporter Reference :', M + col1W + col2W + 2, y + 4);
  doc.setFont(FONT, 'normal');
  const iec = sv(document.iec, sv(company.iec));
  const refLines = doc.splitTextToSize(
    `IEC : ${iec}` + (document.other_ref ? `\n${sv(document.other_ref)}` : ''),
    col3W - 4
  );
  doc.text(refLines, M + col1W + col2W + 2, y + 8);

  y += row1H;

  // ── Row 2: Buyer/Consignee | If buyer other than consignee ───────────
  const halfW = cw / 2;
  const buyerDetailsText = getBuyerDetailsText(document);
  const bLines = doc.splitTextToSize(
    `${sv(document.buyer_name)}\n${sv(document.buyer_address)}` +
    (buyerDetailsText ? `\n${buyerDetailsText}` : ''),
    halfW - 4
  );
  const row2H = Math.max((bLines.length + 1) * 3.5 + 2, 16);

  doc.rect(M, y, halfW, row2H);
  doc.setFont(FONT, 'bold');
  doc.text('Buyer/ Consignee :', M + 2, y + 4);
  doc.setFont(FONT, 'normal');
  doc.text(bLines, M + 2, y + 8);

  doc.rect(M + halfW, y, halfW, row2H);
  doc.setFont(FONT, 'bold');
  doc.text('If buyer other than consignee :', M + halfW + 2, y + 4);

  y += row2H;

  // ── Row 3: Country/Mode + From/To (left 2x2) | Delivery + Payment (right)
  const qW = halfW / 2;
  const countryLines = doc.splitTextToSize(sv(document.country_of_origin, 'INDIA'), qW - 4);
  const modeLines = doc.splitTextToSize(sv(document.mode_of_shipment), qW - 4);
  const fromLines = doc.splitTextToSize(sv(document.shipment_from), qW - 4);
  const toLines = doc.splitTextToSize(sv(document.shipment_to), qW - 4);
  const termsLines = doc.splitTextToSize(sv(document.terms_of_delivery), qW - 4);
  const paymentLines = doc.splitTextToSize(sv(document.mode_of_payment), qW - 4);

  // Top sub-row: one-line label, value starts at +8
  const topRowH = Math.max(
    countryLines.length * 3.5 + 8 + 2,
    modeLines.length * 3.5 + 8 + 2,
    12
  );
  // Bottom sub-row: two-line label, value starts at +11.5
  const botRowH = Math.max(
    fromLines.length * 3.5 + 11.5 + 2,
    toLines.length * 3.5 + 11.5 + 2,
    15
  );
  const row3H = Math.max(
    topRowH + botRowH,
    (termsLines.length + 1) * 3.5 + 3,
    (paymentLines.length + 1) * 3.5 + 3
  );
  const botY = y + topRowH;
  const botH = row3H - topRowH;

  // Country of Origin (top-left)
  doc.rect(M, y, qW, topRowH);
  doc.setFont(FONT, 'bold');
  doc.text('Country of Origin :', M + 2, y + 4);
  doc.setFont(FONT, 'normal');
  doc.text(countryLines, M + 2, y + 8);

  // Mode of Shipment (top-right of left half)
  doc.rect(M + qW, y, qW, topRowH);
  doc.setFont(FONT, 'bold');
  doc.text('Mode of Shipment :', M + qW + 2, y + 4);
  doc.setFont(FONT, 'normal');
  doc.text(modeLines, M + qW + 2, y + 8);

  // Shipment From (bottom-left)
  doc.rect(M, botY, qW, botH);
  doc.setFont(FONT, 'bold');
  doc.text('Shipment From/', M + 2, botY + 4);
  doc.text('Port of Loading :', M + 2, botY + 7.5);
  doc.setFont(FONT, 'normal');
  doc.text(fromLines, M + 2, botY + 11.5);

  // Shipment To (bottom-right of left half)
  doc.rect(M + qW, botY, qW, botH);
  doc.setFont(FONT, 'bold');
  doc.text('Shipment To/', M + qW + 2, botY + 4);
  doc.text('Port of Discharge :', M + qW + 2, botY + 7.5);
  doc.setFont(FONT, 'normal');
  doc.text(toLines, M + qW + 2, botY + 11.5);

  // Terms of delivery (right half, first quarter)
  doc.rect(M + halfW, y, qW, row3H);
  doc.setFont(FONT, 'bold');
  doc.text('Terms of delivery :', M + halfW + 2, y + 4);
  doc.setFont(FONT, 'normal');
  doc.text(termsLines, M + halfW + 2, y + 8);

  // Payment terms (right half, second quarter)
  doc.rect(M + halfW + qW, y, qW, row3H);
  doc.setFont(FONT, 'bold');
  doc.text('Payment terms :', M + halfW + qW + 2, y + 4);
  doc.setFont(FONT, 'normal');
  doc.text(paymentLines, M + halfW + qW + 2, y + 8);

  y += row3H;

  // ── Items Table ─────────────────────────────────────────────────────
  const items = getItems(document);

  const itemRows = items.map((item) => {
    const qty = parseFloat(item.quantity) || 0;
    const rate = parseFloat(item.rate_usd) || 0;
    const amt = parseFloat(item.amount_usd) || (qty * rate);
    return [
      '', // Marks & Nos column stays blank on the proforma
      sv(item.description_of_goods),
      sv(item.hs_code) || sv(document.hs_code),
      qty ? `${qty.toLocaleString('en-IN', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} ${sv(item.uom)}` : '',
      rate ? rate.toLocaleString('en-IN', { minimumFractionDigits: 3, maximumFractionDigits: 3 }) : '',
      amt ? amt.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '',
    ];
  });

  // Additional notes / declaration — printed verbatim as a full-width row after the last item
  const declText = sv(document.declaration).trim();
  if (declText) {
    itemRows.push([
      { content: declText, colSpan: 6, styles: { fontStyle: 'normal', halign: 'left' } },
    ]);
  }

  doc.autoTable({
    startY: y,
    head: [['Marks & Nos', 'D e s c r i p t i o n   o f   G o o d s', 'H.S. Code', 'Quantity', 'Rate(USD)', 'Amount(USD)']],
    body: itemRows,
    theme: 'grid',
    styles: { fontSize: 8, font: FONT, textColor: [0, 0, 0], lineWidth: LW, lineColor: [0, 0, 0], minCellHeight: 12, cellPadding: 3 },
    headStyles: {
      fillColor: false, textColor: [0, 0, 0], fontStyle: 'bolditalic',
      fontSize: 8, halign: 'center', lineWidth: LW, lineColor: [0, 0, 0], minCellHeight: 10,
    },
    columnStyles: {
      0: { cellWidth: 22 },
      1: { cellWidth: cw - 22 - 20 - 32 - 22 - 26 },
      2: { halign: 'center', cellWidth: 20 },
      3: { halign: 'right', cellWidth: 32 },
      4: { halign: 'right', cellWidth: 22 },
      5: { halign: 'right', cellWidth: 26 },
    },
    margin: { left: M, right: M, top: borderStartY },
  });

  y = doc.lastAutoTable.finalY;

  // ── Compute footer section values ───────────────────────────────────
  let totalUsd = parseFloat(document.total_usd) || 0;
  if (totalUsd === 0) {
    items.forEach(it => { totalUsd += parseFloat(it.amount_usd) || 0; });
  }

  const amtWords = amountInWords(document, totalUsd);

  // Advising bank + terms & conditions block
  let bankTermsText = '';
  if (sv(document.advising_bank).trim()) bankTermsText += `Advising Bank: ${sv(document.advising_bank)}\n`;
  if (sv(document.terms_and_conditions).trim()) bankTermsText += sv(document.terms_and_conditions);
  const bankTermsLines = bankTermsText ? doc.splitTextToSize(bankTermsText.trim(), cw - 4) : [];
  const bankTermsH = bankTermsLines.length ? bankTermsLines.length * 3.2 + 5 : 0;

  const amtH = 12;
  const sigH = 22;
  const totalFooterH = amtH + bankTermsH + sigH;

  // Position footer at bottom of page (within border) when there is room
  const footerY = ph - M - totalFooterH;
  if (footerY > y + 2) {
    y = footerY;
  } else if (y + totalFooterH > ph - M) {
    doc.addPage();
    y = ph - M - totalFooterH;
  }

  // ── Amount Chargeable (in words) | Total Value(USD) ─────────────────
  doc.rect(M, y, cw * 0.7, amtH);
  doc.setFont(FONT, 'bold');
  doc.setFontSize(8);
  doc.text('Amount Chargeable (in words) ', M + 2, y + 4);
  doc.setFont(FONT, 'normal');
  doc.setFontSize(7);
  const amtWordsLines = doc.splitTextToSize(amtWords, cw * 0.7 - 4);
  doc.text(amtWordsLines, M + 2, y + 8);

  doc.rect(M + cw * 0.7, y, cw * 0.3, amtH);
  doc.setFont(FONT, 'bold');
  doc.setFontSize(8);
  doc.text('Total Value(USD):', M + cw * 0.7 + 2, y + 6);
  doc.text(`${totalUsd.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, M + cw * 0.7 + 2, y + 10);
  y += amtH;

  // ── Advising Bank / Terms & Conditions ──────────────────────────────
  if (bankTermsH) {
    doc.rect(M, y, cw, bankTermsH);
    doc.setFont(FONT, 'normal');
    doc.setFontSize(7);
    doc.text(bankTermsLines, M + 2, y + 4);
    y += bankTermsH;
  }

  // ── Declarations + Signature Row ────────────────────────────────────
  const halfW2 = cw / 2;
  doc.rect(M, y, halfW2, sigH);
  doc.setFont(FONT, 'bold');
  doc.setFontSize(8);
  doc.text('Declarations :', M + 2, y + 5);
  doc.setFont(FONT, 'italic');
  doc.setFontSize(7);
  doc.text('We hereby declare that this Invoice shown the Actual Price', M + 2, y + 9);
  doc.text('of the goods described and all particulars are true and', M + 2, y + 13);
  doc.text('correct.', M + 2, y + 17);

  doc.rect(M + halfW2, y, halfW2, sigH);
  doc.setFont(FONT, 'bold');
  doc.setFontSize(8);
  doc.text(`For, ${exporterName.toUpperCase()}`, M + halfW2 + 2, y + 5);
  doc.setFontSize(7);
  doc.setFont(FONT, 'normal');
  doc.text(
    `${sv(authority?.authority_type, 'Director')}/ Authorised Signatory`,
    pw - M - 2, y + sigH - 3, { align: 'right' }
  );

  // ── Page border + footer on every page ──────────────────────────────
  const pages = doc.internal.getNumberOfPages();
  const today = new Date();
  const footerDate = `${String(today.getDate()).padStart(2, '0')} ${today.toLocaleDateString('en-GB', { month: 'short' })} ${today.getFullYear()}`;

  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);

    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.5);
    doc.rect(M, borderStartY, pw - 2 * M, ph - borderStartY - M);

    doc.setFontSize(14);
    doc.setFont(FONT, 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('P R O F O R M A   I N V O I C E', pw / 2, M + 5, { align: 'center' });

    doc.setFontSize(7);
    doc.setFont(FONT, 'normal');
    doc.setTextColor(120, 120, 120);
    doc.text(footerDate, M, ph - 5);
    doc.text(`Page ${i} of ${pages}`, pw / 2, ph - 5, { align: 'center' });
    doc.text(exporterName, pw - M, ph - 5, { align: 'right' });
    doc.setTextColor(0, 0, 0);
  }

  if (returnBlob) {
    return doc.output('blob');
  }
  doc.save(`Proforma_${sv(document.invoice_no, document.id).replace(/[\\/\s]+/g, '_')}.pdf`);
}
